import * as React from "react";
import {
  Box,
  Chip,
  FormControl,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Grid,
  IconButton,
  Button,
  Snackbar,
  Alert
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../services/auth.service";

const formatMoney = (value, currency = "USD") => {
  const num = typeof value === "number" ? value : Number(value ?? 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(num) ? num : 0);
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-PA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getCleanPoliza = (polizaStr) => {
  if (!polizaStr) return "";
  return polizaStr.trim().split(/\s+/)[0];
};

const getVigenciaPoliza = (polizaStr) => {
  if (!polizaStr) return "";
  const parts = polizaStr.trim().split(/\s+/);
  return parts.length > 1 ? parts.slice(1).join(" ") : "";
};

const DEFAULT_TOKEN = process.env.REACT_APP_DEFAULT_TOKEN;

export default function CobrosYappy() {
  const { user, logout } = useAuth();

  // Search tab state
  const [searchTab, setSearchTab] = React.useState("cedula");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState(null);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState(null);

  // Selection state
  const [selectedAsegurado, setSelectedAsegurado] = React.useState(null);
  const [facilseguroDetails, setFacilseguroDetails] = React.useState(null);
  const [facilseguroLoading, setFacilseguroLoading] = React.useState(false);
  const [facilseguroError, setFacilseguroError] = React.useState(null);

  // Policy Details Modal state
  const [detailPolicy, setDetailPolicy] = React.useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success"
  });

  const getUniqueAsegurados = React.useCallback((results) => {
    if (!results) return [];
    const map = new Map();

    const getIdEntidad = (item) => {
      return item?.IdEntidad ?? item?.idEntidad ?? item?.IdPersona ?? item?.idPersona ?? item?.IDENTIDAD ?? item?.identidad ?? item?.IdAsegurado;
    };

    const getAseguradoName = (item) => {
      return item?.Nombre ?? item?.Asegurado ?? item?.complete_name ?? item?.NombreCompleto;
    };

    const getAseguradoIdentificacion = (item) => {
      return item?.Cedula ?? item?.Ruc ?? item?.Identificacion ?? item?.Identificación ?? item?.cedula;
    };

    // 1. Process results.personas
    if (Array.isArray(results.personas)) {
      results.personas.forEach(p => {
        const id = getIdEntidad(p);
        if (id && !map.has(id)) {
          map.set(id, {
            idEntidad: id,
            nombre: getAseguradoName(p),
            identificacion: getAseguradoIdentificacion(p),
            celular: p.Celular || p.TelefonoMovil || "",
            telefono: p.Telefono || p.TelefonoCasa || "",
            email: p.Email || p.Correo || "",
            source: 'persona',
            raw: p
          });
        }
      });
    }

    // 2. Process results.polizas
    if (Array.isArray(results.polizas)) {
      results.polizas.forEach(p => {
        const id = getIdEntidad(p);
        if (id && !map.has(id)) {
          map.set(id, {
            idEntidad: id,
            nombre: getAseguradoName(p),
            identificacion: getAseguradoIdentificacion(p),
            celular: p.Celular || p.TelefonoMovil || "",
            telefono: p.Telefono || p.TelefonoCasa || "",
            email: p.Email || p.Correo || "",
            source: 'poliza',
            raw: p
          });
        }
      });
    }

    return Array.from(map.values());
  }, []);

  const executeSearch = React.useCallback(async (tab, queryVal) => {
    if (!queryVal || !queryVal.trim()) return;
    setSearchLoading(true);
    setSearchError(null);
    setSearchResults(null);
    setSelectedAsegurado(null);
    setFacilseguroDetails(null);
    setFacilseguroLoading(false);
    setFacilseguroError(null);
    setDetailPolicy(null);

    try {
      const url = tab === "cedula"
        ? `${process.env.REACT_APP_POWERBIAPI_URL}/api/ext/cedula?q=${encodeURIComponent(queryVal.trim())}`
        : tab === "telefono"
          ? `${process.env.REACT_APP_POWERBIAPI_URL}/api/ext/celular?q=${encodeURIComponent(queryVal.trim())}`
          : tab === "nombre"
            ? `${process.env.REACT_APP_POWERBIAPI_URL}/api/ext/asegurado?q=${encodeURIComponent(queryVal.trim())}`
            : `${process.env.REACT_APP_POWERBIAPI_URL}/api/ext/subgrupo?q=${encodeURIComponent(queryVal.trim())}`;

      const headers = {
        "accept": "*/*",
        "Authorization": `Bearer ${process.env.REACT_APP_POWERBIAPI_TOKEN}`
      };

      const response = await axios.get(url, { headers });
      setSearchResults(response.data);
    } catch (err) {
      console.error("Error executing PowerBI API query:", err);
      setSearchError(
        err.response?.data?.message || "Ocurrió un error al consultar la API externa."
      );
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchExecute = () => {
    executeSearch(searchTab, searchQuery);
  };

  const handleSelectAsegurado = React.useCallback(async (asegurado) => {
    if (!asegurado || !asegurado.idEntidad) return;

    setSelectedAsegurado(asegurado);
    setFacilseguroLoading(true);
    setFacilseguroError(null);
    setFacilseguroDetails(null);

    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "https://genesispanama.art:5000/api";
      const url = `${apiBase}/facilseguro/polizas/${asegurado.idEntidad}`;

      const token = user?.accessToken || DEFAULT_TOKEN;
      const headers = {
        "accept": "*/*",
        "x-access-token": token,
        "Authorization": `Bearer ${token}`
      };

      const response = await axios.get(url, { headers });
      const data = response.data;
      if (data && Array.isArray(data.Polizas)) {
        const insuredName = data.Asegurado?.[0]?.Asegurado || asegurado.nombre || "—";
        data.Polizas = data.Polizas.map(p => {
          const datesStr = (p.FechaDesde && p.FechaHasta) ? ` ${p.FechaDesde} a ${p.FechaHasta}` : "";
          const newNroPoliza = `${p.NroPoliza || ""}${datesStr}`.trim();

          return {
            ...p,
            NroPoliza: newNroPoliza,
            Asegurado: insuredName,
            PrimaNeta: p.Prima ?? p.PrimaSinImpuesto ?? 0
          };
        });
      }
      setFacilseguroDetails(data);
    } catch (err) {
      console.error("Error fetching facilseguro polizas:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        logout();
        return;
      }
      setFacilseguroError(
        err.response?.data?.message || "Ocurrió un error al consultar las pólizas del asegurado."
      );
    } finally {
      setFacilseguroLoading(false);
    }
  }, [user, DEFAULT_TOKEN, logout]);

  // Construct the Yappy Payment Link
  const getYappyPaymentUrl = (idEntidad) => {
    const safeId = idEntidad || 0;
    return `https://segenesis.org/identidad/${safeId}`;
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url)
      .then(() => {
        setSnackbar({
          open: true,
          message: "¡Enlace copiado al portapapeles con éxito!",
          severity: "success"
        });
      })
      .catch((err) => {
        console.error("Error copying link:", err);
        setSnackbar({
          open: true,
          message: "No se pudo copiar el enlace.",
          severity: "error"
        });
      });
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, color: '#191c1e', mb: 1 }}>
          Cobros Yappy
        </Typography>
        <Typography variant="body1" sx={{ color: '#79849a', fontWeight: 500 }}>
          Consulte externamente la información de asegurados y obtenga su enlace de pago Yappy de manera directa.
        </Typography>
      </Box>

      {/* Main Container */}
      <Grid container spacing={3}>
        {/* Left Side: Search Panel */}
        {!selectedAsegurado && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.01)",
                mb: 3
              }}
            >
              <Tabs
                value={searchTab}
                onChange={(e, val) => {
                  setSearchTab(val);
                  setSearchResults(null);
                  setSearchError(null);
                  setSelectedAsegurado(null);
                  setFacilseguroDetails(null);
                  setFacilseguroLoading(false);
                  setFacilseguroError(null);
                  setDetailPolicy(null);
                }}
                indicatorColor="primary"
                textColor="primary"
                sx={{
                  mb: 3,
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                  '& .MuiTab-root': {
                    fontWeight: 800,
                    fontSize: 13,
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 3,
                  }
                }}
              >
                <Tab label="Buscar por Cédula" value="cedula" />
                <Tab label="Buscar por Teléfono" value="telefono" />
                <Tab label="Buscar por Subgrupo" value="subgrupo" />
                <Tab label="Buscar por Nombre" value="nombre" />
              </Tabs>

              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                <TextField
                  fullWidth
                  size="medium"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSearchExecute();
                    }
                  }}
                  placeholder={
                    searchTab === "cedula"
                      ? "Ingrese cédula (ej. 1-234-567)"
                      : searchTab === "telefono"
                        ? "Ingrese número de teléfono (ej. 50764800248)"
                        : searchTab === "nombre"
                          ? "Ingrese nombre del asegurado"
                          : "Ingrese nombre de subgrupo"
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#ffffff",
                      borderRadius: 2.5,
                      "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
                      "&:hover fieldset": { borderColor: "rgba(0,0,0,0.24)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <span className="material-symbols-outlined" style={{ fontSize: 22, color: "#79849a" }}>
                          search
                        </span>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  disableElevation
                  onClick={handleSearchExecute}
                  disabled={searchLoading || !searchQuery.trim()}
                  sx={{
                    bgcolor: "#000000",
                    color: "#ffffff",
                    borderRadius: 2.5,
                    px: 5,
                    fontWeight: 800,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#191c1e" },
                    "&.Mui-disabled": { bgcolor: "#eceef0", color: "#a5a7ab" }
                  }}
                >
                  {searchLoading ? <CircularProgress size={24} color="inherit" /> : "Buscar"}
                </Button>
              </Box>

              {/* Search Error */}
              {searchError && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: "#ffdad6",
                    color: "#93000a",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 3
                  }}
                >
                  <span className="material-symbols-outlined">error</span>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{searchError}</Typography>
                </Paper>
              )}

              {/* Search Loading State */}
              {searchLoading && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
                  <CircularProgress size={44} sx={{ color: "#000000" }} />
                  <Typography sx={{ fontSize: 14, color: "#79849a", fontWeight: 700 }}>
                    Consultando base de datos de Genesis...
                  </Typography>
                </Box>
              )}

              {/* Search Results */}
              {!searchLoading && searchResults && (
                <Box>
                  {(() => {
                    const uniqueAsegurados = getUniqueAsegurados(searchResults);
                    if (uniqueAsegurados.length === 0) {
                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 1.5 }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 48, color: "#79849a" }}>
                            search_off
                          </span>
                          <Typography sx={{ fontWeight: 800, color: "#191c1e", fontSize: 16 }}>No se encontraron asegurados</Typography>
                          <Typography sx={{ fontSize: 13, color: "#79849a" }}>
                            Verifique el criterio de búsqueda e intente nuevamente.
                          </Typography>
                        </Box>
                      );
                    }

                    return (
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#79849a", textTransform: "uppercase", letterSpacing: 0.8, mb: 2 }}>
                          Asegurados Encontrados ({uniqueAsegurados.length})
                        </Typography>
                        <Stack spacing={2}>
                          {uniqueAsegurados.map((asegurado, index) => (
                            <Paper
                              key={index}
                              elevation={0}
                              onClick={() => handleSelectAsegurado(asegurado)}
                              sx={{
                                p: 2.5,
                                borderRadius: 3,
                                bgcolor: '#ffffff',
                                border: '1px solid rgba(0,0,0,0.06)',
                                cursor: 'pointer',
                                transition: 'all 0.25s ease',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                '&:hover': {
                                  bgcolor: '#f8f9fa',
                                  borderColor: 'rgba(0,0,0,0.15)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.03)'
                                }
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 15, fontWeight: 900, color: '#191c1e', mb: 1.5 }}>
                                  {asegurado.nombre || "—"}
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={4}>
                                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700, textTransform: 'uppercase' }}>Cédula / RUC</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#45464d' }}>
                                      {asegurado.identificacion || "—"}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700, textTransform: 'uppercase' }}>Contacto</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#45464d' }}>
                                      {[asegurado.celular, asegurado.telefono].filter(Boolean).join(" / ") || "—"}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700, textTransform: 'uppercase' }}>Correo Electrónico</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#45464d' }}>
                                      {asegurado.email || "—"}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                              <span className="material-symbols-outlined" style={{ color: '#79849a', fontSize: 28, marginLeft: 16 }}>
                                chevron_right
                              </span>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })()}
                </Box>
              )}

              {/* Zero State */}
              {!searchLoading && !searchResults && !searchError && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 1 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 48, color: "#79849a" }}>
                    manage_search
                  </span>
                  <Typography sx={{ fontWeight: 800, color: "#191c1e", fontSize: 16 }}>Consulte Asegurados / Pólizas</Typography>
                  <Typography sx={{ fontSize: 13, color: "#79849a" }}>
                    Ingrese un criterio de búsqueda arriba para comenzar.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Selected Insured Section */}
        {selectedAsegurado && (
          <Grid item xs={12}>
            <Button
              startIcon={<span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>}
              onClick={() => {
                setSelectedAsegurado(null);
                setFacilseguroDetails(null);
                setFacilseguroError(null);
                setDetailPolicy(null);
              }}
              sx={{
                color: "#191c1e",
                fontWeight: 800,
                textTransform: "none",
                mb: 3,
                borderRadius: 2.5,
                border: "1px solid rgba(0,0,0,0.12)",
                px: 2.5,
                py: 1,
                '&:hover': {
                  bgcolor: "rgba(0,0,0,0.04)",
                  borderColor: "#000000"
                }
              }}
            >
              Volver a Asegurados
            </Button>

            {/* Profile & Yappy Payment Link Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Profile Card */}
              <Grid item xs={12} md={7}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    boxSizing: 'border-box',
                    borderRadius: 3,
                    bgcolor: "#ffffff",
                    border: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.01)"
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 36, color: "#000000" }}>
                      account_circle
                    </span>
                    <Box>
                      <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#191c1e" }}>
                        {selectedAsegurado.nombre || "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>
                        IdEntidad: {selectedAsegurado.idEntidad}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 700, textTransform: "uppercase" }}>
                        Cédula / RUC
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#191c1e" }}>
                        {selectedAsegurado.identificacion || "—"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 700, textTransform: "uppercase" }}>
                        Celular / Teléfono
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#191c1e" }}>
                        {[selectedAsegurado.celular, selectedAsegurado.telefono].filter(Boolean).join(" / ") || "—"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 700, textTransform: "uppercase" }}>
                        Correo Electrónico
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#1a73e8" }}>
                        {selectedAsegurado.email ? (
                          <a href={`mailto:${selectedAsegurado.email}`} style={{ color: "inherit", textDecoration: "none" }}>
                            {selectedAsegurado.email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Yappy Link Card */}
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    boxSizing: 'border-box',
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #0052d4 0%, #4364f7 50%, #6fb1fc 100%)",
                    color: "#ffffff",
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: "0 8px 32px rgba(67, 100, 247, 0.15)"
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 24 }}>payments</span>
                      <Typography sx={{ fontSize: 14, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.8 }}>
                        Enlace de Pago Yappy
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, opacity: 0.9, mb: 2 }}>
                      Use este enlace para enviar el cobro directo de Yappy a este asegurado.
                    </Typography>
                  </Box>

                  <Box sx={{ bgcolor: "rgba(255, 255, 255, 0.12)", p: 1.5, borderRadius: 2.5, mb: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, wordBreak: "break-all", fontFamily: "ui-monospace, monospace", opacity: 0.95 }}>
                      {getYappyPaymentUrl(selectedAsegurado.idEntidad)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                      variant="contained"
                      disableElevation
                      startIcon={<span className="material-symbols-outlined" style={{ fontSize: 18 }}>content_copy</span>}
                      onClick={() => handleCopyLink(getYappyPaymentUrl(selectedAsegurado.idEntidad))}
                      sx={{
                        flex: 1,
                        bgcolor: "#ffffff",
                        color: "#0052d4",
                        fontWeight: 800,
                        textTransform: "none",
                        borderRadius: 2,
                        '&:hover': { bgcolor: "rgba(255,255,255,0.9)" }
                      }}
                    >
                      Copiar Enlace
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<span className="material-symbols-outlined" style={{ fontSize: 18 }}>open_in_new</span>}
                      onClick={() => window.open(getYappyPaymentUrl(selectedAsegurado.idEntidad), "_blank")}
                      sx={{
                        borderColor: "#ffffff",
                        color: "#ffffff",
                        fontWeight: 800,
                        textTransform: "none",
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: "#ffffff",
                          bgcolor: "rgba(255,255,255,0.15)"
                        }
                      }}
                    >
                      Abrir
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* FacilSeguro Policies */}
            {facilseguroLoading && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
                <CircularProgress size={40} sx={{ color: "#000000" }} />
                <Typography sx={{ fontSize: 14, color: "#79849a", fontWeight: 700 }}>
                  Obteniendo pólizas y cuotas desde FacilSeguro...
                </Typography>
              </Box>
            )}

            {facilseguroError && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  bgcolor: "#ffdad6",
                  color: "#93000a",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 3
                }}
              >
                <span className="material-symbols-outlined">error</span>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{facilseguroError}</Typography>
              </Paper>
            )}

            {!facilseguroLoading && facilseguroDetails && (
              <Box>
                {(!facilseguroDetails.Polizas || facilseguroDetails.Polizas.length === 0) ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 1.5 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 44, color: "#79849a" }}>
                      folder_open
                    </span>
                    <Typography sx={{ fontWeight: 800, color: "#191c1e", fontSize: 15 }}>No se encontraron pólizas</Typography>
                    <Typography sx={{ fontSize: 13, color: "#79849a" }}>
                      Este asegurado no tiene pólizas activas registradas en FacilSeguro.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#79849a", textTransform: "uppercase", letterSpacing: 0.8, mb: 2 }}>
                      Pólizas del Asegurado ({facilseguroDetails.Polizas.length})
                    </Typography>
                    <Stack spacing={2}>
                      {facilseguroDetails.Polizas.map((poliza, index) => {
                        const cleanNo = getCleanPoliza(poliza.NroPoliza);
                        const vigenciaNo = getVigenciaPoliza(poliza.NroPoliza);
                        return (
                          <Paper
                            key={index}
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: 3,
                              bgcolor: '#ffffff',
                              border: '1px solid rgba(0,0,0,0.06)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.01)'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                              <Box>
                                <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Póliza
                                </Typography>
                                <Typography sx={{ fontSize: 15, fontWeight: 900, color: '#191c1e' }}>
                                  {cleanNo || "—"}
                                </Typography>
                              </Box>
                              <Box>
                                {poliza.SaldoPoliza > 0 ? (
                                  <Chip
                                    label={`Saldo Pendiente: ${formatMoney(poliza.SaldoPoliza)}`}
                                    size="small"
                                    sx={{
                                      bgcolor: '#fce8e6',
                                      color: '#c5221f',
                                      fontWeight: 800,
                                      fontSize: 11,
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label="Saldada"
                                    size="small"
                                    sx={{
                                      bgcolor: '#e6f4ea',
                                      color: '#137333',
                                      fontWeight: 800,
                                      fontSize: 11,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={3}>
                                <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Compañía</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>
                                  {poliza.Compania || "—"}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Ramo / Subramo</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>
                                  {poliza.Ramo || "—"} {poliza.SubRamo ? `· ${poliza.SubRamo}` : ''}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Prima Total</Typography>
                                <Typography sx={{ fontSize: 14, fontWeight: 900, color: '#137333' }}>
                                  {formatMoney(poliza.Prima)}
                                </Typography>
                              </Grid>
                              {vigenciaNo && (
                                <Grid item xs={12} sm={3}>
                                  <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Vigencia</Typography>
                                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>
                                    {vigenciaNo}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>

                            <Divider sx={{ my: 2, borderColor: 'rgba(0,0,0,0.04)' }} />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setDetailPolicy(poliza)}
                                startIcon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>info</span>}
                                sx={{
                                  borderColor: 'rgba(0,0,0,0.15)',
                                  color: '#191c1e',
                                  fontWeight: 800,
                                  textTransform: 'none',
                                  fontSize: 12,
                                  borderRadius: 2,
                                  px: 2.5,
                                  '&:hover': {
                                    borderColor: '#000000',
                                    bgcolor: 'rgba(0,0,0,0.02)'
                                  }
                                }}
                              >
                                Ver Detalles
                              </Button>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        )}
      </Grid>

      {/* Policy Details Sub-modal */}
      <Dialog
        open={Boolean(detailPolicy)}
        onClose={() => setDetailPolicy(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            bgcolor: "#ffffff",
          }
        }}
      >
        {detailPolicy && (
          <>
            <DialogTitle sx={{ p: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography sx={{ fontSize: 11, color: '#79849a', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Detalle Completo de Póliza
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#191c1e', mt: 0.2 }}>
                  {getCleanPoliza(detailPolicy.NroPoliza)}
                </Typography>
              </Box>
              <IconButton onClick={() => setDetailPolicy(null)} size="small" sx={{ color: '#79849a' }}>
                <span className="material-symbols-outlined">close</span>
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* General Info */}
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                  Información de Cobertura
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Ramo</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.Ramo || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Subramo</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.SubRamo || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Compañía Aseguradora</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.Compania || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Tipo de Pago</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.TipoPago || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Vigencia Inicio</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.FechaDesde || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Vigencia Fin</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.FechaHasta || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Forma de Pago</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.FormaDePago || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Próximo Pago</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#c5221f' }}>{detailPolicy.FechaProximoPago || "No programado"}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Financial Summary */}
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                  Resumen Financiero
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Prima Neta</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{formatMoney(detailPolicy.PrimaSinImpuesto)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Impuesto ({detailPolicy.PorcentajeImpuesto || 0}%)</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{formatMoney(detailPolicy.Impuesto)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Prima Total</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: '#137333' }}>{formatMoney(detailPolicy.Prima)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Saldo Pendiente</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: detailPolicy.SaldoPoliza > 0 ? '#c5221f' : '#137333' }}>
                      {formatMoney(detailPolicy.SaldoPoliza)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Executives */}
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                  Ejecutivos y Cuentas
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Ejecutivo de Cobro</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>{detailPolicy.EjecutivoCobro || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Ejecutivo de Cuenta</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>{detailPolicy.EjecutivoCta || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Vendedor / Corredor</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>{detailPolicy.Vendedor || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Líder</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>{detailPolicy.Lider || "—"}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Documents */}
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                  Documentos Adjuntos
                </Typography>
                {(!detailPolicy.Documentos || detailPolicy.Documentos.length === 0) ? (
                  <Typography sx={{ fontSize: 12, color: '#79849a', fontStyle: 'italic' }}>
                    No hay documentos asociados a esta póliza.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {detailPolicy.Documentos.map((doc, idx) => (
                      <Chip
                        key={idx}
                        icon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>picture_as_pdf</span>}
                        label={doc.TipoDocumento || "DOCUMENTO"}
                        onClick={() => {
                          if (doc.PathDocumento) window.open(doc.PathDocumento, '_blank');
                        }}
                        clickable
                        sx={{
                          bgcolor: '#f1f3f4',
                          color: '#1a73e8',
                          fontWeight: 800,
                          fontSize: 11,
                          border: '1px solid rgba(0,0,0,0.06)',
                          '&:hover': {
                            bgcolor: '#e8f0fe',
                            borderColor: '#1a73e8'
                          }
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Installments (Letras) */}
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                  Calendario de Pagos (Letras)
                </Typography>
                {(!detailPolicy.Letras || detailPolicy.Letras.length === 0) ? (
                  <Typography sx={{ fontSize: 12, color: '#79849a', fontStyle: 'italic' }}>
                    No hay cuotas programadas.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800, fontSize: 11, color: '#79849a' }}>Fecha Letra</TableCell>
                          <TableCell sx={{ fontWeight: 800, fontSize: 11, color: '#79849a' }} align="right">Monto</TableCell>
                          <TableCell sx={{ fontWeight: 800, fontSize: 11, color: '#79849a' }} align="center">Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailPolicy.Letras.map((letra, idx) => {
                          const isPaid = String(letra.Estatus || '').toUpperCase() === 'PAGADO';
                          return (
                            <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                              <TableCell sx={{ fontSize: 12, fontWeight: 700, color: '#45464d' }}>{letra.FechaLetra || '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12, fontWeight: 800, color: '#191c1e' }} align="right">{formatMoney(letra.Monto)}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={letra.Estatus || 'PENDIENTE'}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 9,
                                    fontWeight: 900,
                                    bgcolor: isPaid ? '#e6f4ea' : '#fce8e6',
                                    color: isPaid ? '#137333' : '#c5221f'
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Snackbar alerts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontWeight: 700, borderRadius: 2.5 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
